import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ForgotPassword = () => {
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const { sendMagicLink, sendPasswordReset } = useAuth();

  const mode = useMemo(() => (tabValue === 0 ? 'reset' : 'magic'), [tabValue]);

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setWarningMessage('');
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    clearMessages();
  };

  const getFriendlyError = (error) => {
    const raw = error?.message || 'Unable to complete your request right now.';
    const lower = raw.toLowerCase();

    if (lower.includes('rate limit') || lower.includes('security purposes')) {
      return 'Too many attempts. Please wait a minute and try again.';
    }

    if (lower.includes('invalid email')) {
      return 'Please enter a valid email address.';
    }

    return raw;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!email.trim()) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Verify the account exists and is eligible
      const eligibility = await apiService.verifyResetEligibility(email.trim());

      if (eligibility.success && !eligibility.eligible) {
        if (eligibility.reason === 'no_account') {
          setErrorMessage(eligibility.message);
          return;
        }
        if (eligibility.reason === 'oauth_only') {
          setWarningMessage(eligibility.message);
          return;
        }
      }

      // Step 2: Send the reset or magic link
      const action = mode === 'reset'
        ? () => sendPasswordReset(email.trim(), '/update-password')
        : () => sendMagicLink(email.trim(), '/auth/callback');

      const { error } = await action();

      if (error) {
        setErrorMessage(getFriendlyError(error));
        return;
      }

      if (mode === 'reset') {
        setSuccessMessage('Password reset link sent! Check your inbox and spam folder.');
      } else {
        setSuccessMessage('Magic sign-in link sent! Check your inbox and spam folder.');
      }
    } catch (error) {
      setErrorMessage(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Forgot Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose one option to recover access to your account.
            </Typography>

            <Tabs value={tabValue} onChange={handleTabChange} centered>
              <Tab label="Reset Password" />
              <Tab label="Magic Link Sign-In" />
            </Tabs>

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}

            {warningMessage && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {warningMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {successMessage}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TabPanel value={tabValue} index={0}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send a link to set a new password.
                </Typography>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send a one-time magic link to sign in without using your password.
                </Typography>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </TabPanel>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !!successMessage}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ mt: 3 }}
              >
                {loading
                  ? 'Sending...'
                  : mode === 'reset'
                    ? 'Send Reset Link'
                    : 'Send Magic Link'}
              </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Link component={RouterLink} to="/login" underline="hover">
                Back to Sign In
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
